// Hardcoded content for StartRentalBusinessStemToysPage (Phase 1).
// Phase 2 CMS migration: replace these exports with data fetched from the CMS
// returning the same shape. The page component consumes only this module, so
// the swap requires no layout changes.

export const PAGE_META = {
  title: 'Start a STEM Toy Rental Business From Home | Rent Anything Store',
  description:
    'Launch your own STEM toy rental business from home on Rent Anything Store. Low startup costs, high profit margins, and a ready marketplace to list your inventory.',
  canonicalUrl:
    'https://www.rentanythingstore.com/start-a-rental-business/stem-toys',
  h1: 'Launch a Profitable STEM Toy Rental Business From Home',
  subheading:
    'Turn educational toys into recurring rental income on Rent Anything Store — the marketplace built for home-based owners.',
};

export const HERO_IMAGE = {
  alt: 'STEM toy rental business starter kit with robotics and science sets',
  width: 1200,
  height: 675,
};

export const INDUSTRY_OVERVIEW = {
  heading: 'The STEM toy rental industry',
  body:
    'STEM toy rentals are one of the fastest-growing segments of educational commerce. Parents, teachers, and learning centers want high-quality robotics kits, coding games, and science sets without the cost of ownership. That creates steady, repeatable demand — and an opening for a home-based rental business.',
};

export const BENEFITS = {
  heading: 'Benefits of starting a STEM toy rental business',
  bullets: [
    {
      title: 'Sustained demand',
      body:
        'Families, schools, and learning centers are in constant search of enriching educational experiences.',
    },
    {
      title: 'Affordable for renters',
      body:
        'Your customers skip the cost of toys children outgrow — so your inventory stays in constant use.',
    },
    {
      title: 'Flexible scheduling',
      body:
        'Serve short-term projects, summer camps, or long-term learning programs across every age group.',
    },
    {
      title: 'Sustainable by design',
      body:
        'Rentals extend toy lifecycles and reduce waste — a selling point for eco-conscious customers.',
    },
    {
      title: 'Run from home, on your schedule',
      body:
        'Low-overhead, owner-controlled, and ready to scale as demand grows.',
    },
  ],
};

export const WHY_GREAT_BUSINESS = {
  heading: "Why it's a great business to start",
  body:
    'STEM toy rentals combine low startup costs with strong recurring revenue. Inventory pays back quickly: each toy rents multiple times to repeat customers like parents, tutors, and educational programs. You set your hours, your prices, and your territory. And as STEM education keeps gaining parental priority, demand compounds.',
  supportingPoints: [
    'Low startup costs — build inventory gradually or source second-hand',
    'High margins — each item rents many times over its lifespan',
    'Growing market — STEM education is a long-term trend, not a fad',
    'Year-round demand — school terms, summer camps, holiday programs',
    'Online reach — list once, reach renters across your region',
  ],
};

export const EXCLUSIVE_TERRITORY = {
  heading: 'Exclusive territory opportunities',
  body:
    "As a Premier Regional Partner with Rent Anything Store, you can claim exclusive rights to the STEM toy rental category in your region. Once your territory is secured, no other partner can compete with the same offering in your area — positioning your business at the top of category search results. When you succeed, we succeed.",
  urgency: 'Limited territories available. First come, first served.',
};

export const HOW_TO_GET_STARTED = {
  heading: 'How to get started',
  steps: [
    {
      title: 'Sign up',
      body:
        'Register on Rent Anything Store to create your owner profile.',
    },
    {
      title: 'List your inventory',
      body:
        'Add STEM toys you already own or plan to buy — edit or expand anytime.',
    },
    {
      title: 'Set your terms',
      body:
        'Decide your rental rates, availability, cancellation policy, and delivery options.',
    },
    {
      title: 'Leverage marketing tools',
      body:
        'Use built-in promotion features, high-quality photos, and prompt responses to stand out.',
    },
    {
      title: 'Build trust',
      body:
        'Collect reviews and ratings from happy renters to grow your reputation.',
    },
    {
      title: 'Scale up',
      body:
        'Expand inventory based on what renters keep asking for.',
    },
  ],
};

export const MARKETING = {
  heading: 'Marketing your business',
  bullets: [
    'SEO-optimized listings with the keywords your renters actually search',
    'Social engagement on Instagram, Facebook, and parenting groups',
    'Local partnerships with schools, libraries, and after-school programs',
    'Content marketing: blog posts, toy guides, and STEM activity ideas',
  ],
};

export const TOP_ITEMS = {
  heading: 'Top 20 STEM toys to rent',
  items: [
    'Robotics kits (LEGO Mindstorms, VEX Robotics)',
    'Coding games and apps (Osmo, Sphero)',
    'Science experiment kits',
    "Engineering building sets (K'NEX, Meccano)",
    'Math learning tools (abacus, math puzzles)',
    '3D printing pens and kits',
    'Circuitry and electronics kits (Snap Circuits, littleBits)',
    'Microscope kits',
    'Astronomy sets (telescopes, star charts)',
    'Chemistry sets',
    'Solar power kits',
    'Magnetism and electricity kits',
    'Geology kits (rock collections, fossil digs)',
    'Weather stations',
    "Puzzle games (Rubik's Cube, brain teasers)",
    'Educational board games (STEM-themed)',
    'Anatomy models and kits',
    'Eco-friendly building blocks (wooden, bamboo)',
    'Virtual reality learning tools',
    'Drone programming kits',
  ],
};

export const WHY_RAS = {
  heading: 'Why Rent Anything Store',
  bullets: [
    'Wide customer reach across parents, teachers, and educational programs',
    'Cost-effective operations with no storefront overhead',
    'Flexibility to run on your schedule from home',
    'Built-in trust and security via platform safeguards',
    'Marketing support that boosts your visibility',
    'Active community of renters and owners',
  ],
};

export const OWNER_PROTECTION = {
  heading: 'Up to $2,500 Owner Protection',
  body:
    'List your STEM toys with confidence. Rent Anything Store offers up to $2,500 in coverage against losses or damages — so you can rent out premium inventory without outsized risk.',
  linkText: 'Learn about Owner Protection',
  linkTarget: { name: 'CMSPage', params: { pageId: 'owners_protection' } },
};

export const MARKET_STATS = {
  heading: 'STEM toy rental market snapshot',
  stats: [
    {
      label: 'Educational toys market',
      value: '$25B+',
      body: 'Global market size, with STEM as a major growth segment.',
    },
    {
      label: 'STEM education demand',
      value: 'Rising',
      body: 'Parents and educators are prioritizing STEM more every year.',
    },
    {
      label: 'Sustainability trend',
      value: 'Accelerating',
      body: 'Eco-conscious consumers prefer rentals over buying new.',
    },
    {
      label: 'Seasonality',
      value: 'Year-round',
      body: 'School terms, camps, and holidays keep demand steady.',
    },
  ],
};

export const CLOSING = {
  heading: 'Ready to launch your STEM toy rental business?',
  body:
    'Turn your passion for education into an income stream that supports families, reduces waste, and scales on your terms. Rent Anything Store gives you the marketplace, the tools, and the protection to start with confidence.',
};

// Auth-aware CTA config. Consumed by both hero and closing CTAs.
export const CTA = {
  loggedOut: {
    text: 'Start a Rental Business',
    ariaLabel: 'Start your STEM toy rental business',
    target: { name: 'CMSPage', params: { pageId: 'learn_more' } },
  },
  loggedIn: {
    text: 'Add a Listing For Rent',
    ariaLabel: 'Add a STEM toy listing for rent',
    target: { name: 'NewListingPage', params: {} },
  },
};

// JSON-LD Article schema per PRD §8.4.
export const ARTICLE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Start Your STEM Toy Rental Business on Rent Anything Store',
  description:
    'A guide to launching a home-based STEM toy rental business using the Rent Anything Store marketplace.',
  url:
    'https://www.rentanythingstore.com/start-a-rental-business/stem-toys',
  publisher: {
    '@type': 'Organization',
    name: 'Rent Anything Store',
    url: 'https://www.rentanythingstore.com',
  },
};
